import {
  onScopeDispose,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import type { SubtitleTrack } from '@/modules/shared/utils/playerSubtitleTracks';
import { downloadCaptionFile, getCaptionDraftList } from '../services';
import type { CaptionDraft } from '../types';
import { captionsChangedVersion } from './captionsChangedSignal';

export type PreviewSubtitleTrack = SubtitleTrack;

const DRAFT_LIST_PAGE_SIZE = 100;

function isPreviewableDraft(draft: CaptionDraft): boolean {
  return draft.publishStatus === 'draft'
    && draft.processingStatus === 'ready'
    && draft.hasContent === true
    && !(Array.isArray(draft.captionContent) && draft.captionContent.length === 0)
    && draft.deleted !== true;
}

async function fetchCaptionDraftPage(videoKey: string, page: number) {
  const response = await getCaptionDraftList(videoKey, { limit: DRAFT_LIST_PAGE_SIZE, page });
  return response.data;
}

async function fetchAllCaptionDrafts(videoKey: string): Promise<CaptionDraft[]> {
  const firstPage = await fetchCaptionDraftPage(videoKey, 1);
  const remainingPageNumbers = Array.from({ length: Math.max(firstPage.lastPage - 1, 0) }, (_, index) => index + 2);
  const remainingPages = await Promise.all(remainingPageNumbers.map((page) => fetchCaptionDraftPage(videoKey, page)));
  return [firstPage, ...remainingPages].flatMap(({ data }) => data);
}

async function downloadWebVtt(draft: CaptionDraft): Promise<string | null> {
  const response = await downloadCaptionFile(draft.key);
  const webVtt: unknown = response.data;
  if (typeof webVtt !== 'string') {
    throw new Error(`Caption draft ${draft.key} download did not return WebVTT text`);
  }
  return webVtt.includes('-->') ? webVtt : null;
}

function revokeTrackUrls(tracks: PreviewSubtitleTrack[]) {
  tracks.forEach(({ src }) => URL.revokeObjectURL(src));
}

/**
 * Offers a video's finished draft captions as neutral `{ label, language, src }` subtitle tracks backed by
 * `blob:` URLs it owns. Re-computes when the video key changes or `notifyCaptionsChanged` fires for it.
 */
export function usePreviewSubtitleTracks(
  videoKey: MaybeRefOrGetter<string | null | undefined>,
): { tracks: Readonly<Ref<PreviewSubtitleTrack[]>> } {
  const { t } = useI18n();
  const tracks = shallowRef<PreviewSubtitleTrack[]>([]);
  let latestRun = 0;

  async function buildTracks(key: string): Promise<PreviewSubtitleTrack[]> {
    let drafts: CaptionDraft[];
    try {
      drafts = (await fetchAllCaptionDrafts(key)).filter(isPreviewableDraft);
    } catch (error) {
      console.error('Failed to load caption drafts for preview subtitles', error);
      return [];
    }

    const results = await Promise.allSettled(drafts.map(downloadWebVtt));
    return results.flatMap((result, index) => {
      const draft = drafts[index];
      if (result.status === 'rejected') {
        console.error(`Failed to download caption draft ${draft.key} for preview subtitles`, result.reason);
        return [];
      }
      if (result.value === null) {
        return [];
      }
      return [{
        label: `${draft.customName} ${t('videoSubtitle.draftMarker')}`,
        language: draft.languageCode,
        src: URL.createObjectURL(new Blob([result.value], { type: 'text/vtt' })),
      }];
    });
  }

  async function recompute(key: string | null | undefined) {
    latestRun += 1;
    const run = latestRun;
    const nextTracks = key ? await buildTracks(key) : [];
    if (run !== latestRun) {
      revokeTrackUrls(nextTracks);
      return;
    }
    const previousTracks = tracks.value;
    tracks.value = nextTracks;
    revokeTrackUrls(previousTracks);
  }

  watch(
    [
      () => toValue(videoKey),
      () => {
        const key = toValue(videoKey);
        return key ? captionsChangedVersion(key) : 0;
      },
    ],
    ([key]) => {
      recompute(key);
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    latestRun += 1;
    revokeTrackUrls(tracks.value);
    tracks.value = [];
  });

  return { tracks };
}
